import { useState, useCallback, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Send } from 'lucide-react';
import { ValidatorContext } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EarlyAccess({ currentEncounterId, onCommentSaved }) {
  const [form, setForm] = useState({ name: '', email: '', role: 'patient', country: '', comment: '' });
  const [loading, setLoading] = useState(false);
  const { isValidator } = useContext(ValidatorContext);
  const [regSubmitted, setRegSubmitted] = useState(false);
  const [commentSaved, setCommentSaved] = useState(false);
  const [savingComment, setSavingComment] = useState(false);

  const handleSaveComment = useCallback(async () => {
    if (!form.comment.trim()) { toast.error('Please enter a comment.'); return; }
    setSavingComment(true);
    try {
      const payload = { encounterId: currentEncounterId || 'none', reportData: { comment: form.comment, savedAt: new Date().toISOString() } };
      await axios.post(`${API}/reports/save`, payload);
      toast.success('Comment saved.');
      setCommentSaved(true);
      if (onCommentSaved) onCommentSaved();
    } catch { toast.error('Failed to save comment.'); }
    finally { setSavingComment(false); }
  }, [form.comment, currentEncounterId, onCommentSaved]);

  const handleRegister = useCallback(async () => {
    if (!form.email.trim()) { toast.error('Email address is required.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/register-interest`, form);
      toast.success('Registration successful! Thank you for your interest.');
      setRegSubmitted(true);
    } catch (err) { toast.error(err.response?.data?.message || 'Registration failed.'); }
    finally { setLoading(false); }
  }, [form]);

  return (
    <section className="max-w-4xl mx-auto px-6 py-8" data-testid="early-access-section">
      <h3 className="font-bold text-lg text-slate-900 mb-3">Early Access Registration</h3>
      <p className="text-sm text-slate-600 mb-2">Early access is planned to open in small groups to ensure quality and safety.</p>
      <p className="text-sm text-slate-600 mb-2">Those who wish to experience Patient Portable Health Vault (PPHV) may register below to be considered for early access.</p>
      <p className="text-sm text-slate-600 mb-5">Patient Portable Health Vault (PPHV) is offered free forever for patients as part of our commitment to health equity.</p>

      <hr className="border-slate-200 border-dashed mb-5" />

      {/* Encounter Attached */}
      <div className="mb-4">
        <span className="text-sm text-slate-500 italic">
          Encounter attached: {currentEncounterId || 'none — submit an intake first'}
        </span>
      </div>

      {/* Comment / Feedback */}
      <div className="mb-5">
        <Label className="text-sm font-bold text-slate-800">Comment / Feedback <span className="text-red-500">*</span></Label>
        <Textarea
          value={form.comment}
          onChange={e => setForm(p => ({ ...p, comment: e.target.value }))}
          placeholder=""
          className="mt-1.5 bg-white border-2 border-slate-300 rounded-md min-h-[80px] text-sm focus:border-[#2563EB]"
          data-testid="reg-comment"
        />
        <Button
          onClick={handleSaveComment}
          disabled={savingComment || commentSaved}
          className="mt-2 rounded-md bg-[#2563EB] hover:bg-blue-700 text-white text-sm px-5"
          data-testid="save-comment-btn"
        >
          {savingComment ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
          {commentSaved ? 'Saved' : 'Save Comment'}
        </Button>
      </div>

      <hr className="border-slate-200 mb-5" />

      {/* Register Interest */}
      {regSubmitted ? (
        <p className="text-sm text-emerald-700 font-medium" data-testid="registration-success">
          Thank you! Your interest has been registered. We will be in touch.
        </p>
      ) : (
        <div data-testid="registration-form">
          <h4 className="font-bold text-base text-slate-900 mb-1">Register Interest</h4>
          <p className="text-sm text-[#2563EB] mb-4">Register with your email to be considered for early access to PPHV.</p>

          <div className="space-y-3 max-w-md">
            <div>
              <Label className="text-sm font-bold text-slate-800">Name <span className="text-slate-400">(optional)</span></Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="" className="mt-1 bg-white border-2 border-slate-300 rounded-md focus:border-[#2563EB]" data-testid="reg-name" />
            </div>
            <div>
              <Label className="text-sm font-bold text-slate-800">Email Address <span className="text-red-500">*</span></Label>
              <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="" type="email" className="mt-1 bg-white border-2 border-slate-300 rounded-md focus:border-[#2563EB]" data-testid="reg-email" />
            </div>
            <div>
              <Label className="text-sm font-bold text-slate-800">I am a <span className="text-red-500">*</span></Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="mt-1 bg-white border-2 border-slate-300 rounded-md focus:border-[#2563EB]" data-testid="reg-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="patient">Patient</SelectItem>
                  <SelectItem value="physician">Physician/Provider</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold text-slate-800">Country</Label>
              <Input value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))}
                placeholder="" className="mt-1 bg-white border-2 border-slate-300 rounded-md focus:border-[#2563EB]" data-testid="reg-country" />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={handleRegister} disabled={loading}
                className="rounded-md bg-[#2563EB] hover:bg-blue-700 text-white text-sm px-5" data-testid="reg-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Registering...</> : 'Register Interest'}
              </Button>
              {/* Validator-mode pill removed May 19, 2026 — redundant with top Clinical Validator Registration block in IntakeForm. */}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
